/**
 * ADMIN FEATURES PAGE
 *
 * Manage apps and features in leBusiness ERP
 * - Enable/disable features globally
 * - Set feature pricing
 * - Configure per-company overrides
 */

import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Boxes,
  Search,
  Edit,
  Package,
  Users,
  FileText,
  ShoppingCart,
  Wallet,
  Receipt,
  Scale,
  Calculator,
  BarChart3,
  Truck,
  LayoutDashboard,
  Settings,
  Languages,
  WifiOff,
  Laptop,
  Code,
  Palette,
  FileCheck,
  CreditCard,
  Mail,
  MessageSquare,
  Crown,
  Zap,
  Plus,
  Trash2,
  Building2,
  Loader2,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  useAdminFeatures,
  useAdminFeatureStats,
  useUpdateFeature,
  useUpdateFeaturePricing,
  useFeatureOverrides,
  useSetCompanyOverride,
  useRemoveCompanyOverride,
} from '@/hooks/queries/useAdminFeatures';
import { useAdminCompanies } from '@/hooks/queries/useAdminLicenses';
import type { FeatureDefinition } from '@/services/featureFlagService';

export const Route = createFileRoute('/admin/features/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminFeaturesPage,
});

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  Package,
  Users,
  FileText,
  Settings,
  ShoppingCart,
  Wallet,
  Receipt,
  Scale,
  Calculator,
  BarChart3,
  Truck,
  Languages,
  WifiOff,
  Laptop,
  Code,
  Palette,
  FileCheck,
  CreditCard,
  Mail,
  MessageSquare,
};

function FeatureCard({ feature, onEdit }: { feature: FeatureDefinition; onEdit: (f: FeatureDefinition) => void }) {
  const Icon = iconMap[feature.icon || ''] || Boxes;
  const pricing = feature.feature_pricing?.[0];
  const updateFeature = useUpdateFeature();

  const handleToggleDefault = () => {
    updateFeature.mutate({
      id: feature.id,
      updates: { default_enabled: !feature.default_enabled },
    });
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700 hover:border-violet-500/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${feature.is_premium ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
            <Icon className={`w-6 h-6 ${feature.is_premium ? 'text-amber-400' : 'text-emerald-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white">{feature.name}</h3>
              {feature.is_premium && (
                <Crown className="w-4 h-4 text-amber-400" />
              )}
            </div>
            <p className="text-sm text-slate-400 mt-1">{feature.code}</p>
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-slate-500">
                {feature.override_count || 0} overrides
              </span>
              {pricing && Number(pricing.price_monthly) > 0 && (
                <span className="text-xs text-violet-400">
                  {Number(pricing.price_monthly).toLocaleString()} DZD/mo
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Switch
              checked={feature.default_enabled}
              onCheckedChange={handleToggleDefault}
              disabled={updateFeature.isPending}
            />
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={() => onEdit(feature)}>
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EditFeatureDialog({
  feature,
  onClose,
}: {
  feature: FeatureDefinition;
  onClose: () => void;
}) {
  const { profile } = useAuthStore();
  const updateFeature = useUpdateFeature();
  const updatePricing = useUpdateFeaturePricing();
  const setOverride = useSetCompanyOverride();
  const removeOverride = useRemoveCompanyOverride();

  const pricing = feature.feature_pricing?.[0];

  const [name, setName] = useState(feature.name);
  const [description, setDescription] = useState(feature.description || '');
  const [isPremium, setIsPremium] = useState(feature.is_premium);
  const [defaultEnabled, setDefaultEnabled] = useState(feature.default_enabled);
  const [priceMonthly, setPriceMonthly] = useState(Number(pricing?.price_monthly) || 0);
  const [priceYearly, setPriceYearly] = useState(Number(pricing?.price_yearly) || 0);
  const [activeTab, setActiveTab] = useState<'settings' | 'overrides'>('settings');

  // Company overrides
  const { data: overrides, isLoading: overridesLoading } = useFeatureOverrides(feature.id);
  const { data: companies } = useAdminCompanies();
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [overrideEnabled, setOverrideEnabled] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');

  const handleSave = () => {
    updateFeature.mutate(
      {
        id: feature.id,
        updates: { name, description: description || undefined, is_premium: isPremium, default_enabled: defaultEnabled },
      },
      {
        onSuccess: () => {
          if (priceMonthly > 0 || priceYearly > 0) {
            updatePricing.mutate({
              featureId: feature.id,
              pricing: { currency: 'DZD', price_monthly: priceMonthly, price_yearly: priceYearly, region: 'global' },
            });
          }
          onClose();
        },
      }
    );
  };

  const handleAddOverride = () => {
    if (!selectedCompanyId || !profile) return;
    setOverride.mutate(
      {
        companyId: selectedCompanyId,
        featureId: feature.id,
        enabled: overrideEnabled,
        reason: overrideReason || undefined,
        createdBy: profile.id,
      },
      {
        onSuccess: () => {
          setSelectedCompanyId('');
          setOverrideReason('');
          setOverrideEnabled(true);
        },
      }
    );
  };

  const handleRemoveOverride = (companyId: string) => {
    removeOverride.mutate({ companyId, featureId: feature.id });
  };

  // Filter out companies that already have an override
  const availableCompanies = (companies || []).filter(
    (c: any) => !overrides?.some((o) => o.company_id === c.id)
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Feature: {feature.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Configure feature settings, pricing, and per-company overrides
          </DialogDescription>
        </DialogHeader>

        {/* Tab switcher */}
        <div className="flex gap-2 border-b border-slate-700 pb-2">
          <Button
            variant={activeTab === 'settings' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('settings')}
            className={activeTab === 'settings' ? 'bg-violet-600' : 'text-slate-400'}
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            variant={activeTab === 'overrides' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('overrides')}
            className={activeTab === 'overrides' ? 'bg-violet-600' : 'text-slate-400'}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Company Overrides ({overrides?.length || 0})
          </Button>
        </div>

        {activeTab === 'settings' && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input value={feature.code} className="bg-slate-800 border-slate-700" disabled />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="bg-slate-800 border-slate-700" rows={2} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800">
              <div>
                <p className="font-medium">Premium Feature</p>
                <p className="text-sm text-slate-400">Requires subscription to use</p>
              </div>
              <Switch checked={isPremium} onCheckedChange={setIsPremium} />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800">
              <div>
                <p className="font-medium">Enabled by Default</p>
                <p className="text-sm text-slate-400">Available to all companies without override</p>
              </div>
              <Switch checked={defaultEnabled} onCheckedChange={setDefaultEnabled} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Price (DZD)</Label>
                <Input type="number" value={priceMonthly} onChange={(e) => setPriceMonthly(Number(e.target.value))} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="space-y-2">
                <Label>Yearly Price (DZD)</Label>
                <Input type="number" value={priceYearly} onChange={(e) => setPriceYearly(Number(e.target.value))} className="bg-slate-800 border-slate-700" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'overrides' && (
          <div className="space-y-4 py-2">
            {/* Add new override */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 space-y-3">
                <h4 className="text-sm font-medium text-white">Add Override</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select company..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {availableCompanies.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-slate-400 whitespace-nowrap">
                      {overrideEnabled ? 'Enable' : 'Disable'}
                    </Label>
                    <Switch checked={overrideEnabled} onCheckedChange={setOverrideEnabled} />
                  </div>
                </div>
                <Input
                  placeholder="Reason (optional)"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  className="bg-slate-700 border-slate-600"
                />
                <Button
                  size="sm"
                  onClick={handleAddOverride}
                  disabled={!selectedCompanyId || setOverride.isPending}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  {setOverride.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Override
                </Button>
              </CardContent>
            </Card>

            {/* Existing overrides */}
            {overridesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 bg-slate-700" />
                <Skeleton className="h-12 bg-slate-700" />
              </div>
            ) : overrides && overrides.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Reason</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrides.map((override) => (
                    <TableRow key={override.id} className="border-slate-700">
                      <TableCell className="text-white font-medium">
                        {override.company?.name || override.company_id}
                      </TableCell>
                      <TableCell>
                        {override.enabled ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Enabled</Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {override.override_reason || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOverride(override.company_id)}
                          className="text-red-400 hover:text-red-300"
                          disabled={removeOverride.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No company overrides for this feature. All companies follow the default setting.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">
            Cancel
          </Button>
          {activeTab === 'settings' && (
            <Button
              onClick={handleSave}
              disabled={updateFeature.isPending}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {updateFeature.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminFeaturesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [editingFeature, setEditingFeature] = useState<FeatureDefinition | null>(null);

  const { data: features, isLoading } = useAdminFeatures();
  const { data: stats } = useAdminFeatureStats();

  const filteredFeatures = (features || []).filter((feature) => {
    const matchesSearch = feature.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      feature.code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = selectedTab === 'all' || feature.category === selectedTab;
    return matchesSearch && matchesTab;
  });

  const groupedFeatures = {
    apps: filteredFeatures.filter(f => f.category === 'app'),
    features: filteredFeatures.filter(f => f.category === 'feature'),
    integrations: filteredFeatures.filter(f => f.category === 'integration'),
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Boxes className="w-8 h-8 text-violet-400" />
              Apps & Features
            </h1>
            <p className="text-slate-400 mt-2">
              Manage all apps, features, and integrations in the platform
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <Boxes className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.totalFeatures ?? '-'}</p>
                  <p className="text-sm text-slate-400">Total Features</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Crown className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.premiumFeatures ?? '-'}</p>
                  <p className="text-sm text-slate-400">Premium</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.defaultEnabled ?? '-'}</p>
                  <p className="text-sm text-slate-400">Enabled by Default</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats?.integrations ?? '-'}</p>
                  <p className="text-sm text-slate-400">Integrations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="bg-slate-800 border border-slate-700">
              <TabsTrigger value="all" className="data-[state=active]:bg-violet-600">All</TabsTrigger>
              <TabsTrigger value="app" className="data-[state=active]:bg-violet-600">Apps</TabsTrigger>
              <TabsTrigger value="feature" className="data-[state=active]:bg-violet-600">Features</TabsTrigger>
              <TabsTrigger value="integration" className="data-[state=active]:bg-violet-600">Integrations</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Loading state */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-32 bg-slate-800/50" />
            ))}
          </div>
        ) : selectedTab === 'all' ? (
          <div className="space-y-8">
            {groupedFeatures.apps.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-violet-400" />
                  Apps ({groupedFeatures.apps.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedFeatures.apps.map((feature) => (
                    <FeatureCard key={feature.id} feature={feature} onEdit={setEditingFeature} />
                  ))}
                </div>
              </div>
            )}

            {groupedFeatures.features.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-400" />
                  Features ({groupedFeatures.features.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedFeatures.features.map((feature) => (
                    <FeatureCard key={feature.id} feature={feature} onEdit={setEditingFeature} />
                  ))}
                </div>
              </div>
            )}

            {groupedFeatures.integrations.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  Integrations ({groupedFeatures.integrations.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedFeatures.integrations.map((feature) => (
                    <FeatureCard key={feature.id} feature={feature} onEdit={setEditingFeature} />
                  ))}
                </div>
              </div>
            )}

            {filteredFeatures.length === 0 && (
              <p className="text-center text-slate-500 py-8">No features match your search.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFeatures.length > 0 ? (
              filteredFeatures.map((feature) => (
                <FeatureCard key={feature.id} feature={feature} onEdit={setEditingFeature} />
              ))
            ) : (
              <p className="text-center text-slate-500 py-8 col-span-full">No features match your search.</p>
            )}
          </div>
        )}

        {/* Edit Feature Dialog */}
        {editingFeature && (
          <EditFeatureDialog
            feature={editingFeature}
            onClose={() => setEditingFeature(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminFeaturesPage;
